import { useEffect, useRef } from "react";
import { Check, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { previewSpecs } from "./PreviewStage";

// Gallery of this window's finished previews. `currentId` is shared with the page, so
// whatever you land on here is what the workspace shows when the modal closes.
export default function PreviewLightbox({ open, setOpen, previews, currentId, onChange }) {
  const items = previews.filter((p) => p.status === "SUCCESS" && p.previewUrl);
  const idx = Math.max(0, items.findIndex((p) => p._id === currentId));
  const current = items[idx];
  const stripRef = useRef(null);

  const go = (dir) => {
    const next = items[idx + dir];
    if (next) onChange(next._id);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Keep the active thumbnail in view as you page through.
  useEffect(() => {
    if (open && current) stripRef.current?.querySelector(`[data-id="${current._id}"]`)?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [open, current]);

  const number = (p) => previews.length - previews.findIndex((x) => x._id === p._id);

  return (
    <Dialog open={open && !!current} onOpenChange={setOpen}>
      <DialogContent
        data-testid="ai-preview-fullscreen"
        className="max-w-[calc(100vw-1.5rem)] lg:max-w-5xl p-5 sm:p-8 gap-0 rounded-2xl sm:rounded-2xl border-border bg-background shadow-2xl [&>button]:right-5 [&>button]:top-5 sm:[&>button]:right-7 sm:[&>button]:top-7 [&>button>svg]:h-5 [&>button>svg]:w-5"
      >
        <div className="flex items-center gap-3 pr-10 mb-5">
          <Sparkles className="h-6 w-6 text-accent" strokeWidth={1.6} />
          <DialogTitle className="font-serif text-2xl sm:text-[1.75rem] font-normal text-foreground">AI Preview</DialogTitle>
          {current?.isSelectedFinal && (
            <span className="inline-flex items-center gap-1 rounded-full bg-success/10 text-success px-2.5 py-1 text-xs font-medium">
              <Check className="h-3.5 w-3.5" /> Final design
            </span>
          )}
        </div>
        <DialogDescription className="sr-only">Use the arrow keys or thumbnails to move between previews.</DialogDescription>

        {current && (
          <div className="relative rounded-xl overflow-hidden bg-secondary">
            <img
              key={current._id}
              src={current.previewUrl}
              alt={`AI preview #${number(current)}`}
              className="w-full max-h-[62vh] object-contain hi-animate-fade-up"
            />
            {items.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous preview"
                  data-testid="lightbox-prev"
                  disabled={idx === 0}
                  onClick={() => go(-1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-card text-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-0"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  aria-label="Next preview"
                  data-testid="lightbox-next"
                  disabled={idx === items.length - 1}
                  onClick={() => go(1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-card text-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-0"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
            {previewSpecs(current) && (
              <span className="absolute bottom-3 left-3 rounded-full bg-foreground/70 text-background px-3 py-1 text-xs">
                #{number(current)} · {previewSpecs(current)}
              </span>
            )}
          </div>
        )}

        {items.length > 1 && (
          <div ref={stripRef} className="mt-5 flex gap-3 overflow-x-auto p-1 -m-1" role="listbox" aria-label="Previews">
            {items.map((p) => {
              const active = p._id === current?._id;
              return (
                <button
                  key={p._id}
                  data-id={p._id}
                  role="option"
                  aria-selected={active}
                  aria-label={`Preview #${number(p)}${previewSpecs(p) ? `, ${previewSpecs(p)}` : ""}`}
                  data-testid={`lightbox-thumb-${p._id}`}
                  onClick={() => onChange(p._id)}
                  className={cn(
                    "relative shrink-0 w-32 sm:w-40 aspect-[16/10] rounded-lg overflow-hidden transition-all",
                    active ? "ring-2 ring-accent ring-offset-2 ring-offset-background" : "opacity-80 hover:opacity-100",
                  )}
                >
                  <img src={p.previewUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                  {p.isSelectedFinal && (
                    <span className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-success text-white flex items-center justify-center">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
